import { Box, Stack, Typography } from '@mui/material';
import { useHistory } from 'react-router-dom';

import Button from 'domains/core/components/Button';
import { URLPaths } from 'models/enums';

import { COPY_GENERATOR_BRAND_REQUIRED_PANEL_BG } from './utils';

export type Props = {
    onDismiss: () => void;
};

const ELEVATION_2_SHADOW =
    '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)';

/**
 * Inline card when Ask Sara cannot run because brand is not registered
 * (API errorCode `638bbb87-6a99-4ad0-933d-2ec972cfb817`).
 * Matches Figma Content-Builder: node 3901-21931 (Paper block 3901-22255).
 */
const BrandNotRegisteredCTA = ({ onDismiss }: Props) => {
    const history = useHistory();

    const handleGoToBrandSettings = () => {
        history.push(URLPaths.SETTINGS_BRAND);
        onDismiss();
    };

    return (
        <Box
            aria-label="Brand registration required to use Ask Sara"
            data-testid="copy-generator-brand-not-registered"
            role="region"
            sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                px: 2,
                py: 2,
                overflowY: 'auto',
            }}
        >
            <Box
                sx={{
                    p: 2,
                    borderRadius: '4px',
                    bgcolor: COPY_GENERATOR_BRAND_REQUIRED_PANEL_BG,
                    boxShadow: ELEVATION_2_SHADOW,
                }}
            >
                <Stack alignItems="flex-start" direction="row" spacing={0.5}>
                    <Box
                        aria-hidden
                        sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            bgcolor: 'secondary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            mt: 0.25,
                        }}
                    >
                        <Typography
                            component="span"
                            sx={{
                                color: 'secondary.contrastText',
                                fontSize: 15,
                                fontWeight: 700,
                                lineHeight: 1,
                                textTransform: 'lowercase',
                            }}
                        >
                            i
                        </Typography>
                    </Box>
                    <Stack spacing={2} sx={{ flex: 1, minWidth: 0, maxWidth: 320 }}>
                        <Stack spacing={0.25}>
                            <Typography
                                component="h3"
                                sx={{
                                    color: 'secondary.dark',
                                    fontSize: 16,
                                    fontWeight: 500,
                                    letterSpacing: '0.15px',
                                    lineHeight: '24px',
                                }}
                            >
                                i&apos;ll write better with your brand voice
                            </Typography>
                            <Typography
                                sx={{
                                    color: 'text.primary',
                                    fontSize: 13,
                                    fontWeight: 400,
                                    letterSpacing: '0.16px',
                                    lineHeight: '18px',
                                }}
                            >
                                you haven&apos;t set up brand preferences yet. it takes 5 min — and i&apos;ll use it
                                every time i write for you.
                            </Typography>
                        </Stack>
                        <Stack alignItems="center" direction="row" flexWrap="wrap" spacing={1}>
                            <Button
                                color="secondary"
                                data-testid="copy-generator-brand-not-registered-cta"
                                size="small"
                                variant="contained"
                                onClick={handleGoToBrandSettings}
                                aria-label="Set up brand voice"
                                sx={{ letterSpacing: '0.5px' }}
                            >
                                Set up brand voice
                            </Button>
                            <Button
                                color="secondary"
                                data-testid="copy-generator-brand-not-registered-dismiss"
                                size="small"
                                variant="text"
                                onClick={onDismiss}
                                aria-label="Maybe later, close Ask Sara"
                                sx={{ letterSpacing: '0.5px' }}
                            >
                                Maybe later
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </Box>
        </Box>
    );
};

export default BrandNotRegisteredCTA;
